import mime from 'mime/lite';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

type ProgressCallback = (progress: number) => void;

/**
 * Uploads a file to Firebase Storage with progress tracking and returns the download URL.
 */
export const uploadWithProgress = async (
  file: File,
  filePath: string,
  onProgress: ProgressCallback
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileRef = ref(storage, `${filePath}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      },
      (error) => {
        console.error('Upload error:', error);
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(fileRef);
          resolve(downloadURL);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

/**
 * Checks if a URL points to a video file based on extension.
 * Strips query params before checking to avoid false positives.
 */
export const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const urlWithoutQuery = url.split('?')[0];
  const mimeType = mime.getType(urlWithoutQuery);
  return !!mimeType && mimeType.startsWith('video/');
};

/**
 * Checks if a URL points to an image file based on extension.
 * Strips query params before checking to avoid false positives.
 */
export const isImageUrl = (url: string): boolean => {
  if (!url) return false;
  const urlWithoutQuery = url.split('?')[0];
  const mimeType = mime.getType(urlWithoutQuery);
  return !!mimeType && mimeType.startsWith('image/');
};

/**
 * Checks if a URL or storage path points to a PDF based on extension / mime.
 */
export const isPdfUrl = (url: string): boolean => {
  if (!url) return false;
  const pathOnly = decodeURIComponent(url.split('?')[0]);
  const mimeType = mime.getType(pathOnly);
  return mimeType === 'application/pdf' || pathOnly.toLowerCase().endsWith('.pdf');
};

/**
 * Builds a download filename for a stored asset, preferring the real object
 * name and falling back to `<base>.<ext>` inferred from the URL. Storage paths
 * are URL-encoded, so decode before splitting on '/'.
 */
export const getDownloadFileName = (url: string, fallbackBase: string): string => {
  const pathOnly = decodeURIComponent(url.split('?')[0]);
  const lastSegment = pathOnly.split('/').pop() || '';
  if (/\.[a-z0-9]{2,5}$/i.test(lastSegment)) return lastSegment;
  const safeBase = fallbackBase.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '') || 'content';
  return `${safeBase}.${isVideoUrl(url) ? 'mp4' : 'jpg'}`;
};

/**
 * Saves a remote asset to the user's device. The file is fetched as a blob
 * first because the anchor `download` attribute is ignored on cross-origin
 * URLs. Returns false when the fetch is blocked (signed storage URLs are not
 * always CORS-readable) so callers can offer an open-in-a-new-tab fallback
 * rather than silently doing nothing.
 */
export const downloadFile = async (url: string, fileName: string): Promise<boolean> => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    // Give the browser time to start the download before releasing the blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    return false;
  }
};